import axios from 'axios';

import { DEFAULT_BACKEND_SWIMLANE } from '@/constants/fetch';
import { STORAGE_KEY_BACKEND_SWIMLANE } from '@/constants/storage';
import { isProdEnv } from '@/utils/env';
import { localStore } from '@/utils/storage';
import { logPageDataLoadedTrack } from '@/utils/tracker/global-info';

import { buildH5CommonParams } from './build-common-params';
import { ensureSessionForRequest } from './session-gate';
import { formatRequestOptions, formatRequestParams, handleFetchFailed, handleFetchSuccess } from './tools';

import type {
    IFetchConfig,
    IGetDataResponseConfig,
    IGetOriginResponseConfig,
    IRequestOptions,
    IResponse,
    TOriginResponse,
} from './typings';

let hasFirstValidRequest = false;

// fetch 方法，会调用 axios 发起请求，并处理返回信息
export async function fetch<RequestData, ResponseData>(
    config: IFetchConfig<RequestData>
): Promise<IResponse<ResponseData> | TOriginResponse<ResponseData>> {
    const { method, url, data: configData } = config;
    await ensureSessionForRequest(config.waitForSession);

    const formatOptions = formatRequestOptions(config);
    const formatParams = formatRequestParams<RequestData>({ options: formatOptions, method, data: configData });
    const { axiosConfig, isNeedOriginRes } = formatOptions;
    const commonParams = {
        ...(await buildH5CommonParams()),
        ...(config.query || {}),
    };
    const fetchData =
        method.toLowerCase() === 'get'
            ? {
                  params: {
                      ...commonParams,
                      ...formatParams,
                  },
              }
            : {
                  params: commonParams,
                  data: formatParams,
              };

    const startTime = Date.now();

    try {
        const headersConfig = {
            ...(axiosConfig?.headers ?? {}),
        };

        // 非线上：请求头带 x-env，供网关路由到指定测试环境
        // 未配置（null）→ 默认 offline；显式空字符串 → 不传 x-env
        if (!isProdEnv) {
            const backendSwimlane = localStore.getItem(STORAGE_KEY_BACKEND_SWIMLANE);
            if (backendSwimlane !== '') {
                headersConfig['x-env'] = backendSwimlane ?? DEFAULT_BACKEND_SWIMLANE;
            }
        }

        const originResponse = await axios.request<IResponse<ResponseData>>({
            method,
            url,
            ...axiosConfig,
            headers: headersConfig,
            ...fetchData,
        });

        const duration = Date.now() - startTime;

        // 处理成功响应
        const handlerResult = handleFetchSuccess<ResponseData>({
            originResponse,
            options: formatOptions,
            duration,
        });

        // 如果请求结果和服务端结果都成功，则返回数据
        if (handlerResult.success) {
            // 仅第一次请求成功时上报页面数据加载完成埋点
            if (!hasFirstValidRequest) {
                logPageDataLoadedTrack();
                hasFirstValidRequest = true;
            }

            if (!isNeedOriginRes && 'data' in originResponse && typeof originResponse.data === 'object') {
                const { code, message, data } = originResponse.data;
                return { code, message, data };
            }
            return originResponse;
        }

        // 否则，抛出错误，在 catch 中处理
        throw handlerResult.originResponse;
    } catch (error) {
        const duration = Date.now() - startTime;
        // 处理完错误信息后，抛出错误，外部可通过 catch 捕获
        handleFetchFailed(error as any, formatOptions, duration);
        return Promise.reject(error);
    }
}

/**
 * 用于业务代码中快速定义一个Api
 *
 * TS泛型： RequestData - 请求参数的声明，如果没有请求参数，就填undefined
 * TS泛型： ResponseData - 响应中服务端传回的data信息，即 OriginResponse.data.data
 * TS泛型： Response - 响应中服务端传回的完整信息，即 OriginResponse.data，带有 code， data， message 信息
 * TS泛型： OriginResponse - 完整的响应信息，带有cookies, data, errMsg, header, statusCode 信息
 *
 * 存在 2 种重载信息：
 */
// 重载声明，当config.isNeedOriginRes = true， 会返回originResponse
export function defineApi<RequestData, ResponseData>(
    config: IGetOriginResponseConfig
): (requestData?: RequestData, otherConfig?: IRequestOptions) => Promise<TOriginResponse<ResponseData>>;

// 重载声明，当config.isNeedOriginRes = false， 会返回originResponse.data信息
export function defineApi<RequestData, ResponseData>(
    config: IGetDataResponseConfig
): (requestData?: RequestData, otherConfig?: IRequestOptions) => Promise<IResponse<ResponseData>>;

export function defineApi<RequestData, ResponseData>(config: IGetOriginResponseConfig | IGetDataResponseConfig) {
    return (
        requestData?: RequestData,
        otherConfig?: IRequestOptions
    ): Promise<IResponse<ResponseData> | TOriginResponse<ResponseData>> => {
        // 组合所有的配置
        const formatConfig: IFetchConfig<RequestData> = {
            ...config,
            data: requestData,
            ...otherConfig,
        };
        return fetch(formatConfig);
    };
}
